import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";

const Shell = ({ title, sub, children }) => (
  <section className="min-h-screen grid lg:grid-cols-2">
    <div className="relative hidden lg:block">
      <img src="/images/todd-stadium.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C55] via-[#0A1C55]/40 to-black/30" />
      <p className="absolute bottom-14 left-14 right-14 font-serif text-4xl font-semibold tracking-[-0.04em] text-white leading-tight">"Be transformed by the renewing of your mind."<span className="block text-base font-accent text-gold-light mt-4 tracking-normal">Romans 12:2</span></p>
    </div>
    <div className="flex items-center justify-center px-5 pt-36 pb-16">
      <div className="w-full max-w-md">
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-[-0.04em] text-white">{title}</h1>
        {sub && <p className="text-slate-400 mt-3">{sub}</p>}
        <div className="mt-10">{children}</div>
      </div>
    </div>
  </section>
);

const Field = ({ id, label, ...p }) => (
  <div><label className="label" htmlFor={id}>{label}</label><input id={id} className="field" data-testid={`${id}-input`} {...p} /></div>
);

const useNext = () => {
  const [params] = useSearchParams();
  const n = params.get("next") || "/dashboard";
  return n.startsWith("/") && !n.startsWith("//") ? n : "/dashboard";
};

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const next = useNext();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSEO({ title: "Log in" });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(f.email, f.password); nav(next); } catch (er) { setErr(errMsg(er)); }
    setBusy(false);
  };
  return (
    <Shell title="Welcome back." sub="Log in to continue your transformation journey.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field id="login-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="login-password" label="Password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p className="text-sm text-red-400" data-testid="login-error">{err}</p>}
        <button disabled={busy} className="btn-gold w-full" data-testid="login-submit-btn">{busy ? "Logging in..." : "Log in"}</button>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-slate-400 hover:text-white" data-testid="forgot-password-link">Forgot password?</Link>
          <Link to={`/register?next=${encodeURIComponent(next)}`} className="text-gold-light" data-testid="go-register-link">Create account</Link>
        </div>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const next = useNext();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSEO({ title: "Start Your Transformation" });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await register(f.name, f.email, f.password); toast.success("Welcome to Your Transformation Journey."); nav(next); } catch (er) { setErr(errMsg(er)); }
    setBusy(false);
  };
  return (
    <Shell title="Start Your Transformation." sub="Create your free account. Upgrade anytime.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <Field id="register-name" label="Full name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field id="register-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field id="register-password" label="Password (8+ characters)" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {err && <p className="text-sm text-red-400" data-testid="register-error">{err}</p>}
        <button disabled={busy} className="btn-gold w-full" data-testid="register-submit-btn">{busy ? "Creating account..." : "Create free account"}</button>
        <p className="text-sm text-slate-400">Already a member? <Link to={`/login?next=${encodeURIComponent(next)}`} className="text-gold-light" data-testid="go-login-link">Log in</Link></p>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title="Reset password." sub="We'll email you a secure reset link.">
      {sent ? <p className="text-slate-200" data-testid="forgot-sent">If an account exists for that email, a reset link is on its way.</p> : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <Field id="forgot-email" label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="btn-gold w-full" data-testid="forgot-submit-btn">Send reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw }); toast.success("Password updated. Please log in."); nav("/login"); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Shell title="Choose a new password.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <Field id="reset-password" label="New password" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} />
        <button className="btn-gold w-full" data-testid="reset-submit-btn">Update password</button>
      </form>
    </Shell>
  );
}
